import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getStatusLabel, getPriorityLabel, getStatusLabels, getPriorityLabels } from '../types';
import type { TicketStatus, TicketPriority, TaskType } from '../types';
import { FileText, Plus, X, Trash2, ChevronDown, ChevronLeft, ChevronRight, Search, Eye, Paperclip, Settings2, MessageCircle, Camera, Image } from 'lucide-react';
import DeadlineBadge from '../components/DeadlineBadge';
import SlaBadge from '../components/SlaBadge';
import VoiceRecorder from '../components/VoiceRecorder';
import ConfirmModal from '../components/ConfirmModal';
import TimerSummaryModal from '../components/TimerSummaryModal';

const ATTACH_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';

const PAGE_SIZE = 20;

export default function TicketsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [openStatusMenu, setOpenStatusMenu] = useState<string | null>(null);
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isInternal = isAdmin || isAgent;
  type TicketTab = 'mine' | 'mine_and_unassigned' | 'all';
  const [activeTab, setActiveTab] = useState<TicketTab>(() => {
    const saved = localStorage.getItem('tsi-tickets-tab') as TicketTab | null;
    if (saved) return saved;
    return isInternal ? 'mine_and_unassigned' : 'mine';
  });
  const [tabInitialized, setTabInitialized] = useState(false);

  useEffect(() => {
    if (!tabInitialized && isAdmin && !localStorage.getItem('tsi-tickets-tab')) {
      setActiveTab('all');
      setTabInitialized(true);
    }
  }, [isAdmin, tabInitialized]);
  const [showPrefs, setShowPrefs] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [closeNoLogsTicket, setCloseNoLogsTicket] = useState<{ id: string; title: string } | null>(null);
  const [showAddLogForClose, setShowAddLogForClose] = useState<{ id: string; title: string } | null>(null);
  const [closeSubtasksWarning, setCloseSubtasksWarning] = useState<{ id: string; count: number } | null>(null);
  const [cascadeSubtasks, setCascadeSubtasks] = useState(false);
  const [slaBreachOnly, setSlaBreachOnly] = useState(false);

  const filterParam = activeTab === 'all' ? undefined : activeTab;
  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', statusFilter, priorityFilter, searchQuery, page, activeTab],
    queryFn: () => api.getTickets({
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      search: searchQuery || undefined,
      filter: filterParam,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
  });

  const createMutation = useMutation({
    mutationFn: (d: { title: string; description: string; priority: TicketPriority; task_type: TaskType; files: File[]; customer_id?: string; assigned_agent_id?: string; contact_id?: string; due_date?: string }) => {
      const payload = { ...d, customer_id: d.customer_id || user!.id };
      return d.files.length > 0
        ? api.createTicketWithFiles(payload, d.files)
        : api.createTicket(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setShowCreate(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, cascade }: { id: string; status: string; cascade?: boolean }) =>
      api.updateTicketStatus(id, status, cascade),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTicket(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
  });

  const canCreate = !!user;
  const canDelete = user && user.role === 'admin';
  const canChangeStatus = user && ['admin', 'agent', 'customer_manager'].includes(user.role);

  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = () => {
    setSearchQuery(searchText);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title">{t('tickets.title')}</h1>
        {canCreate && (
          <button className="btn btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)}>
            <Plus size={18} />
            <span>{t('tickets.newTicket')}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="tabs-pill">
          {isInternal && (
            <button
              type="button"
              className={activeTab === 'mine_and_unassigned' ? 'tab-pill-active' : 'tab-pill-inactive'}
              onClick={() => { setActiveTab('mine_and_unassigned'); localStorage.setItem('tsi-tickets-tab', 'mine_and_unassigned'); setPage(0); }}
            >
              {t('tickets.mineAndUnassigned')}
            </button>
          )}
          <button
            type="button"
            className={activeTab === 'mine' ? 'tab-pill-active' : 'tab-pill-inactive'}
            onClick={() => { setActiveTab('mine'); localStorage.setItem('tsi-tickets-tab', 'mine'); setPage(0); }}
          >
            {t('tickets.myTickets')}
          </button>
          <button
            type="button"
            className={activeTab === 'all' ? 'tab-pill-active' : 'tab-pill-inactive'}
            onClick={() => { setActiveTab('all'); localStorage.setItem('tsi-tickets-tab', 'all'); setPage(0); }}
          >
            {t('tickets.all')}
          </button>
        </div>
        {activeTab === 'mine' && (
          <button type="button" onClick={() => setShowPrefs(true)}
            className="p-1.5 text-surface-400 hover:text-surface-600 transition-colors rounded-lg hover:bg-surface-200" title={t('tickets.filterPrefs')}>
            <Settings2 size={16} />
          </button>
        )}
      </div>

      {showPrefs && (
        <TicketPreferencesModal
          onClose={() => setShowPrefs(false)}
          onSaved={() => { setShowPrefs(false); queryClient.invalidateQueries({ queryKey: ['tickets'] }); }}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t('tickets.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="input py-2 pr-10"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400" size={18} />
          </div>
          <button onClick={handleSearch} className="btn btn-secondary py-2">{t('tickets.searchButton')}</button>
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="input py-2 text-sm w-auto">
          <option value="">{t('tickets.allStatuses')}</option>
          {Object.entries(getStatusLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }} className="input py-2 text-sm w-auto">
          <option value="">{t('tickets.allPriorities')}</option>
          {Object.entries(getPriorityLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-1.5 cursor-pointer text-sm text-surface-600 whitespace-nowrap">
          <input
            type="checkbox"
            checked={slaBreachOnly}
            onChange={(e) => { setSlaBreachOnly(e.target.checked); setPage(0); }}
            className="w-3.5 h-3.5 accent-red-600"
          />
          {t('sla.breachedOnly')}
        </label>
      </div>

      {statusMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('tickets.statusChangeError')}</div>
      )}

      {deleteMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('tickets.deleteError')}</div>
      )}

      {showCreate && <CreateTicketModal
        onClose={() => setShowCreate(false)}
        onSubmit={(d) => createMutation.mutate(d)}
        loading={createMutation.isPending}
        error={createMutation.error}
      />}


      {isLoading && (
        <div className="table-container">
          <div className="divide-y divide-surface-400/10">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-table-row">
                <div className="skeleton-avatar" />
                <div className="skeleton-cell" />
                <div className="skeleton-cell max-w-[100px] flex-none" />
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <div className="card bg-danger-50 border border-danger-100"><p className="text-danger-700">{t('tickets.loadError')}</p></div>}

      {data?.success && (
        <>
          <div className="text-sm text-surface-500">
            <span className="tabular-nums">{total}</span>
            {t('tickets.ticketCount')}
          </div>
          <div className="grid gap-4">
            {data.data && data.data.length > 0 ? data.data
              .filter((ticket) => !slaBreachOnly || ticket.sla_response_breached || ticket.sla_resolution_breached)
              .map((ticket) => (
              <div key={ticket.id} className="card-hover transition-colors">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${ticket.task_type === 'discussion' ? 'bg-indigo-100' : 'bg-primary-100'}`}>
                    {ticket.task_type === 'discussion'
                      ? <MessageCircle className="text-indigo-600" size={24} />
                      : <FileText className="text-primary-600" size={24} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Link to={`/tickets/${ticket.id}`} className="text-lg font-semibold text-surface-900 hover:text-primary-600 transition-colors">
                          {ticket.ticket_number ? <span className="text-xs text-surface-400 me-1 tabular-nums">TKT-{ticket.ticket_number}</span> : null}{ticket.title}
                        </Link>
                        {ticket.task_type === 'discussion' && (
                          <span className="badge bg-indigo-50 text-indigo-700">{t('taskType.discussion')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {canChangeStatus ? (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setOpenStatusMenu(openStatusMenu === ticket.id ? null : ticket.id)}
                              className={`text-sm cursor-pointer ${
                                ticket.status === 'open' ? 'badge badge-primary' :
                                ticket.status === 'in_progress' ? 'badge badge-warning' :
                                ticket.status === 'waiting' ? 'badge badge-gray' :
                                'badge badge-success'
                              }`}
                            >
                              {getStatusLabel(ticket.status, t)}
                              <ChevronDown size={14} />
                            </button>
                            {openStatusMenu === ticket.id && (
                              <div className="absolute left-0 top-full mt-1 dropdown-menu z-10 min-w-[120px]">
                                {(['open', 'in_progress', 'waiting', 'closed'] as TicketStatus[]).map((s) => (
                                  <button key={s} className="block w-full text-right px-4 py-2 text-sm hover:bg-surface-50 first:rounded-t-lg last:rounded-b-lg"
                                    onClick={async () => {
                                      setOpenStatusMenu(null);
                                      if (s === 'closed' && ticket.status !== 'closed') {
                                        try {
                                          const wlData = await api.getWorkLogs({ ticket_id: ticket.id, limit: 1 });
                                          if (!wlData.data || wlData.data.length === 0) {
                                            setCloseNoLogsTicket({ id: ticket.id, title: ticket.title });
                                            return;
                                          }
                                        } catch { /* proceed */ }
                                        if (!ticket.parent_ticket_id) {
                                          try {
                                            const stData = await api.getSubtasks(ticket.id);
                                            const openSubs = (stData.data ?? []).filter((st: any) => st.status !== 'closed');
                                            if (openSubs.length > 0) {
                                              setCascadeSubtasks(false);
                                              setCloseSubtasksWarning({ id: ticket.id, count: openSubs.length });
                                              return;
                                            }
                                          } catch { /* proceed */ }
                                        }
                                      }
                                      statusMutation.mutate({ id: ticket.id, status: s });
                                    }}>
                                    {getStatusLabel(s, t)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={`text-sm ${
                            ticket.status === 'open' ? 'badge badge-primary' :
                            ticket.status === 'in_progress' ? 'badge badge-warning' :
                            ticket.status === 'waiting' ? 'badge badge-gray' :
                            'badge badge-success'
                          }`}>{getStatusLabel(ticket.status, t)}</span>
                        )}
                        <span className={
                          ticket.priority === 'urgent' ? 'badge badge-danger' :
                          ticket.priority === 'high' ? 'badge badge-warning' :
                          ticket.priority === 'medium' ? 'badge badge-primary' :
                          'badge badge-gray'
                        }>{getPriorityLabel(ticket.priority, t)}</span>
                        <Link to={`/tickets/${ticket.id}`} className="p-1 text-surface-400 hover:text-primary-600 transition-colors">
                          <Eye size={16} />
                        </Link>
                        {canDelete && (
                          <button onClick={() => setConfirmDeleteId(ticket.id)}
                            className="p-1 text-surface-400 hover:text-danger-600 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    {ticket.description && <p className="text-surface-600 mb-2 text-sm">{ticket.description}</p>}
                    {ticket.customer_summary && (
                      <p className="text-xs text-indigo-600 bg-indigo-50 rounded px-2 py-1 mb-2 truncate" title={ticket.customer_summary}>
                        {t('tickets.statusSummary')}{ticket.customer_summary}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-surface-500">
                      <span>{t('tickets.customer')}{ticket.customer_name || t('common.notSpecified')}</span>
                      <span>&bull;</span>
                      {ticket.agents && ticket.agents.length > 0 ? (
                        <span>{t('tickets.technicians')}{ticket.agents.map((a) => a.name).join(', ')}</span>
                      ) : (
                        <span>{t('tickets.agent')}{ticket.agent_name || t('common.unassigned')}</span>
                      )}
                      {ticket.due_date && (
                        <>
                          <span>&bull;</span>
                          <DeadlineBadge dueDate={ticket.due_date} />
                        </>
                      )}
                    </div>
                    {(ticket.sla_response_due_at || ticket.sla_resolution_due_at) && (
                      <div className="mt-2">
                        <SlaBadge
                          responseDueAt={ticket.sla_response_due_at}
                          resolutionDueAt={ticket.sla_resolution_due_at}
                          responseBreached={ticket.sla_response_breached}
                          resolutionBreached={ticket.sla_resolution_breached}
                          firstResponseAt={ticket.first_response_at}
                          status={ticket.status}
                          compact
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="empty-state-illustrated rounded-xl border border-surface-400/15 bg-surface-0 shadow-card">
                <div className="empty-icon-wrapper">
                  <FileText className="empty-icon" size={48} strokeWidth={1.25} />
                </div>
                <p className="empty-title">{t('tickets.noTickets')}</p>
                <p className="empty-text">{t('timer.noTicketsFound')}</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronRight size={20} />
              </button>
              <span className="text-sm text-surface-600 tabular-nums">
                {t('common.page')} {page + 1} {t('common.of')} {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft size={20} />
              </button>
            </div>
          )}
        </>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title={t('tickets.deleteTitle')}
          description={t('tickets.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => { deleteMutation.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}

      {closeNoLogsTicket && (
        <div className="modal-backdrop z-[60]"
          onClick={(e) => { if (e.target === e.currentTarget) setCloseNoLogsTicket(null); }}>
          <div className="modal-content max-w-sm w-full p-6" dir="rtl">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-lg font-bold text-surface-900">{t('tickets.noWorkLogWarning')}</h3>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddLogForClose(closeNoLogsTicket);
                  setCloseNoLogsTicket(null);
                }}
                className="btn btn-primary flex-1 flex items-center justify-center gap-1"
              >
                <Plus size={16} /> {t('tickets.addWorkLog')}
              </button>
              <button
                onClick={() => {
                  statusMutation.mutate({ id: closeNoLogsTicket.id, status: 'closed' });
                  setCloseNoLogsTicket(null);
                }}
                className="btn btn-secondary flex-1"
              >
                {t('tickets.closeAnyway')}
              </button>
            </div>
          </div>
        </div>
      )}

      {closeSubtasksWarning && (
        <div className="modal-backdrop z-[60]"
          onClick={(e) => { if (e.target === e.currentTarget) setCloseSubtasksWarning(null); }}>
          <div className="modal-content max-w-sm w-full p-6" dir="rtl">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-lg font-bold text-surface-900">{t('tickets.openSubtasks', { count: closeSubtasksWarning.count })}</h3>
            </div>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={cascadeSubtasks}
                onChange={(e) => setCascadeSubtasks(e.target.checked)}
                className="w-4 h-4 accent-primary-600" />
              <span className="text-sm text-surface-700">{t('tickets.cascadeClose')}</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  statusMutation.mutate({ id: closeSubtasksWarning.id, status: 'closed', cascade: cascadeSubtasks });
                  setCloseSubtasksWarning(null);
                }}
                className="btn btn-secondary flex-1"
              >
                {t('tickets.closeAnyway')}
              </button>
              <button onClick={() => setCloseSubtasksWarning(null)}
                className="btn btn-secondary flex-1">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddLogForClose && (
        <TimerSummaryModal
          ticketId={showAddLogForClose.id}
          ticketTitle={showAddLogForClose.title}
          startTime={new Date(Date.now() - 3600000).toISOString()}
          endTime={new Date().toISOString()}
          durationMinutes={60}
          onClose={() => setShowAddLogForClose(null)}
          onSuccess={() => {
            statusMutation.mutate({ id: showAddLogForClose!.id, status: 'closed' });
            setShowAddLogForClose(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
          }}
        />
      )}
    </div>
  );
}

function FilePreviewChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string>('');
  const isImg = file.type.startsWith('image/');
  useEffect(() => {
    if (!isImg) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file, isImg]);

  if (isImg && url) {
    return (
      <div className="relative group">
        <img src={url} alt={file.name} className="w-20 h-20 rounded-xl object-cover border border-surface-400/15 shadow-card" />
        <button type="button" onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-danger-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-card">
          <X size={10} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 bg-surface-100 rounded-lg text-xs text-surface-700 group relative">
      <Paperclip size={11} />
      <span className="max-w-[120px] truncate">{file.name}</span>
      <button type="button" onClick={onRemove}
        className="text-surface-400 hover:text-danger-500"><X size={11} /></button>
    </div>
  );
}

function CreateTicketModal({ onClose, onSubmit, loading, error }: {
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; priority: TicketPriority; task_type: TaskType; files: File[]; customer_id?: string; assigned_agent_id?: string; contact_id?: string; due_date?: string }) => void;
  loading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isInternal = user?.role === 'admin' || user?.role === 'agent';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [newTicketType, setNewTicketType] = useState<'ticket' | 'discussion'>('ticket');
  const [files, setFiles] = useState<File[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const [contactId, setContactId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const { data: usersData } = useQuery({
    queryKey: ['users-for-create'],
    queryFn: () => api.getUsers(),
    enabled: isInternal,
  });
  const users = usersData?.data || [];
  const customers = users.filter((u: any) => u.role === 'customer_viewer' || u.role === 'customer_manager');
  const agents = users.filter((u: any) => u.role === 'admin' || u.role === 'agent');

  const addFilesFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arr = Array.from(e.target.files || []).filter(f => f.size <= 10 * 1024 * 1024);
    setFiles(prev => [...prev, ...arr]);
    e.target.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title, description, priority, task_type: newTicketType, files,
      customer_id: customerId || undefined,
      assigned_agent_id: assignedAgentId || undefined,
      contact_id: contactId || undefined,
      due_date: dueDate || undefined,
    });
  };

  return (
    <div className="modal-backdrop md:flex md:items-center md:justify-center">
      <div className="modal-content md:max-w-lg w-full h-full md:h-auto md:max-h-[90vh] overflow-y-auto p-6 rounded-none md:rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('tickets.newTicket')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-4 mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="taskType" value="ticket" checked={newTicketType === 'ticket'} onChange={() => setNewTicketType('ticket')} />
              {t('taskType.ticket')}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="taskType" value="discussion" checked={newTicketType === 'discussion'} onChange={() => setNewTicketType('discussion')} />
              {t('taskType.discussion')}
            </label>
          </div>
          <div>
            <label className="label">{t('common.title')}</label>
            <input className="input min-h-[44px] text-base" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={500} />
          </div>
          <div>
            <label className="label">{t('common.description')}</label>
            <textarea className="input min-h-[120px] text-base" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </div>
          <div>
            <label className="label">{t('common.priority')}</label>
            <select className="input min-h-[44px] text-base" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
              {Object.entries(getPriorityLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {/* Extended fields for internal roles */}
          {isInternal && (
            <>
              <div>
                <label className="label">{t('tickets.customer')}</label>
                <select className="input min-h-[44px] text-base" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">{t('tickets.selectCustomer')}</option>
                  {customers.map((u: any) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
              </div>
              <div>
                <label className="label">{t('tickets.assignAgent')}</label>
                <select className="input min-h-[44px] text-base" value={assignedAgentId} onChange={(e) => setAssignedAgentId(e.target.value)}>
                  <option value="">{t('tickets.autoAssign')}</option>
                  {agents.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">{t('tickets.dueDate')}</label>
                <input type="date" className="input min-h-[44px] text-base" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <div>
                <label className="label">{t('contacts.contact')} ({t('common.optional')})</label>
                <input className="input min-h-[44px] text-base" value={contactId} onChange={(e) => setContactId(e.target.value)} placeholder={t('contacts.contactIdPlaceholder')} />
              </div>
            </>
          )}

          <div>
            <label className="label">{t('tickets.files')}</label>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {files.map((f, i) => (
                  <FilePreviewChip key={i} file={f} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => galleryRef.current?.click()}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 border-dashed border-surface-400/15 hover:border-primary-400 hover:bg-primary-50 transition-colors min-h-[44px]">
                <Image size={20} className="text-purple-500" />
                <span className="text-xs text-surface-600">{t('tickets.gallery')}</span>
              </button>
              <button type="button" onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 border-dashed border-surface-400/15 hover:border-primary-400 hover:bg-primary-50 transition-colors min-h-[44px]">
                <Camera size={20} className="text-pink-500" />
                <span className="text-xs text-surface-600">{t('tickets.takePhoto')}</span>
              </button>
              <button type="button" onClick={() => fileRef.current?.click()}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 border-dashed border-surface-400/15 hover:border-primary-400 hover:bg-primary-50 transition-colors min-h-[44px]">
                <Paperclip size={20} className="text-info-500" />
                <span className="text-xs text-surface-600">{t('tickets.addFile')}</span>
              </button>
            </div>
            <div className="mt-2">
              <VoiceRecorder onRecordingComplete={(file) => setFiles(prev => [...prev, file])} />
            </div>
            <input ref={fileRef} type="file" accept={ATTACH_ACCEPT + ',audio/webm,audio/ogg,audio/mpeg,video/mp4'} multiple className="hidden" onChange={addFilesFromInput} />
            <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden" onChange={addFilesFromInput} />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={addFilesFromInput} />
          </div>
          {!!error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('tickets.createError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="btn btn-primary flex-1 min-h-[48px] text-base font-semibold">
              {loading ? t('common.creating') : t('tickets.createTicket')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1 min-h-[48px]">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TicketPreferencesModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const prefs = user?.ticket_filter_preferences || { assigned: true, commented: true, created: false, include_closed: false };
  const [assigned, setAssigned] = useState(prefs.assigned);
  const [commented, setCommented] = useState(prefs.commented);
  const [created, setCreated] = useState(prefs.created);
  const [includeClosed, setIncludeClosed] = useState(prefs.include_closed);

  const mutation = useMutation({
    mutationFn: () => api.updateTicketPreferences({ assigned, commented, created, include_closed: includeClosed }),
    onSuccess: onSaved,
  });

  return (
    <div className="modal-backdrop z-[60]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-sm w-full p-6" dir="rtl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-surface-900">{t('tickets.filterTitle')}</h3>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={20} /></button>
        </div>
        <div className="space-y-3 mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={assigned} onChange={(e) => setAssigned(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <span className="text-sm text-surface-700">{t('tickets.filterAssigned')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={commented} onChange={(e) => setCommented(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <span className="text-sm text-surface-700">{t('tickets.filterCommented')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={created} onChange={(e) => setCreated(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <span className="text-sm text-surface-700">{t('tickets.filterCreated')}</span>
          </label>
          <hr />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <span className="text-sm text-surface-700">{t('tickets.filterIncludeClosed')}</span>
          </label>
        </div>
        <div className="flex gap-3">
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="btn btn-primary flex-1">
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </button>
          <button onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}
